import { Container, type Text } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { BigWinPort, LocalizationService, LocalizedText } from "@slot/game-shell";
import { TEXT, label, value } from "../textStyles";

/** The server's tier ids that carry a title (`nice` has none: the dimmed number alone), with their English words. */
const TIER_TITLES: Record<string, string> = { big: "BIG WIN", mega: "MEGA WIN", epic: "EPIC WIN" };

const GEOMETRY = {
  content: { width: 1200, height: 900, y: { desktop: 0, mobile: -200, mobile_landscape: 0 } },
  title: { y: -190, fontSize: 170, box: { width: 1100, height: 220 } },
  amount: { y: 90, fontSize: 190, box: { width: 1100, height: 240 } },
} as const;

/** The count-up breathing's own scope on the chrome runner (nothing slams it: the next motion on the number ends it). */
const PULSE_SCOPE = "big_win_amount_pulse";

/** The big win's motion (seconds of the chrome runner). */
export interface BigWinMotionTuning {
  dimInSeconds: number;
  /** The number's pop-in on `backOut`, from `amountFromScale`. */
  amountPopSeconds: number;
  amountFromScale: number;
  /** Each tier title's pop-in on `backOut`, from `titleFromScale`. */
  titlePopSeconds: number;
  titleFromScale: number;
  /** The count-up's breathing: one cycle's seconds and its peak scale. */
  pulseSeconds: number;
  pulseScale: number;
  /** The bump when the count lands. */
  settleSeconds: number;
  settleScale: number;
  exitSeconds: number;
  continueFadeInSeconds: number;
  continueFadeOutSeconds: number;
}

/**
 * The big win behind `BigWinPort`: the tier's title over the counting amount, both in the panels' type (Pixi text,
 * so the amount keeps its currency symbol). The number pops in alone; each titled tier the count passes pops its title
 * in place of the last; the number breathes while it counts and bumps when it lands. The presenter starts the pop-in
 * and the breathing in the same tick, so the number's motions are separate channels — its fade, and three scales
 * (pop, breath, bump) multiplied together — each with its own token: one never cancels another.
 */
export class BigWinView extends Container implements BigWinPort {
  public readonly amountOmitsCurrencySymbol = false;
  private readonly chrome: ModalChrome;
  private readonly runner: PresentationTaskRunner;
  private readonly tuning: BigWinMotionTuning;
  private readonly amount: Text;
  /** Each titled tier's line and the scale its fit box gave it (its pop multiplies that, never a scale mid-pop). */
  private readonly titles: ReadonlyMap<string, { node: LocalizedText; fit: number }>;
  /** The number's three scale factors, multiplied: the pop-in, the count-up's breathing, the settle bump. */
  private readonly amountScale = { pop: 1, breath: 1, bump: 1 };
  /** Bumped by each new motion of its channel; a stale tween stops writing. */
  private readonly generation = { fade: 0, pop: 0, breath: 0, bump: 0, title: 0 };

  public constructor(input: { viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; tuning: BigWinMotionTuning }) {
    super({ label: "BigWinPanel" });
    const g = GEOMETRY;
    this.runner = input.runner;
    this.tuning = input.tuning;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxHeight: 1300 }, mobile: { maxWidth: 1060 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);

    this.titles = new Map(
      Object.entries(TIER_TITLES).map(([tier, fallback]) => {
        const node = label({ localization: input.localization, key: `bigwin.${tier}`, fallback, style: TEXT.heading(), ...g.title, label: `Title:${tier}` });
        node.visible = false;
        this.chrome.content.addChild(node);
        return [tier, { node, fit: node.scale.x }];
      }),
    );
    this.amount = value({ style: TEXT.bigWinAmount, fontSize: g.amount.fontSize, y: g.amount.y, label: "BigWinAmount" });
    this.chrome.content.addChild(this.amount);
    this.hideImmediate();
  }

  public setAmount(formatted: string): void {
    this.amount.text = formatted;
  }

  public setSkipVisible(visible: boolean): void {
    this.chrome.skipButton.visible = visible;
  }

  public hideTitles(): void {
    this.generation.title += 1;
    for (const title of this.titles.values()) title.node.visible = false;
    this.chrome.stopFade(this.chrome.continueTextRoot);
    this.chrome.continueTextRoot.alpha = 0;
    this.amount.alpha = 0;
  }

  public revealTier(tierId: string, _animateEnter: boolean): boolean {
    const title = this.titles.get(tierId.toLowerCase());
    if (!title) return false;
    for (const other of this.titles.values()) other.node.visible = other === title;
    const generation = ++this.generation.title;
    const { titlePopSeconds, titleFromScale } = this.tuning;
    const { node: shown, fit } = title;
    void this.runner.tweenProgress(
      titlePopSeconds,
      (k) => {
        if (generation !== this.generation.title || shown.destroyed) return;
        const scale = fit * (titleFromScale + (1 - titleFromScale) * k);
        shown.scale.set(scale, scale);
        shown.alpha = Math.min(1, k * 3);
      },
      undefined,
      { easing: "backOut" },
    );
    return true;
  }

  public showImmediate(): void {
    this.chrome.visible = true;
  }

  public hideImmediate(): void {
    for (const channel of ["fade", "pop", "breath", "bump", "title"] as const) this.generation[channel] += 1;
    Object.assign(this.amountScale, { pop: 1, breath: 1, bump: 1 });
    this.chrome.stopFade(this.chrome.dim);
    this.chrome.stopFade(this.chrome.continueTextRoot);
    this.chrome.visible = false;
  }

  /** The number's pop-in and fade-in, with the dim. */
  public async playEnterAnimation(): Promise<void> {
    const { dimInSeconds, amountPopSeconds, amountFromScale } = this.tuning;
    const pop = ++this.generation.pop;
    const fade = ++this.generation.fade;
    this.chrome.dim.alpha = 0;
    await Promise.all([
      this.chrome.fade(this.chrome.dim, 1, dimInSeconds),
      this.runner.tweenProgress(
        amountPopSeconds,
        (k) => {
          if (this.amount.destroyed) return;
          if (pop === this.generation.pop) this.setAmountScale("pop", amountFromScale + (1 - amountFromScale) * k);
          if (fade === this.generation.fade) this.amount.alpha = Math.min(1, k * 3);
        },
        undefined,
        { easing: "backOut" },
      ),
    ]);
  }

  /** The number, the title, the dim and the hint fading out from where they are; the number's motions stop. */
  public async playExitAnimation(): Promise<void> {
    const { exitSeconds, continueFadeOutSeconds } = this.tuning;
    const fade = ++this.generation.fade;
    for (const channel of ["pop", "breath", "bump", "title"] as const) this.generation[channel] += 1;
    const fading = [this.amount, ...[...this.titles.values()].map((title) => title.node).filter((node) => node.visible)].map((node) => ({ node, from: node.alpha }));
    await Promise.all([
      this.chrome.fade(this.chrome.dim, 0, exitSeconds),
      this.chrome.fade(this.chrome.continueTextRoot, 0, continueFadeOutSeconds),
      this.runner.tweenProgress(
        exitSeconds,
        (k) => {
          if (fade !== this.generation.fade) return;
          for (const { node, from } of fading) if (!node.destroyed) node.alpha = from * (1 - k);
        },
        undefined,
        { easing: "linear" },
      ),
    ]);
  }

  /** The count-up breathing, until the settle bump, the exit or a hide stops it. */
  public startAmountPulse(): void {
    const { pulseSeconds, pulseScale } = this.tuning;
    const breath = ++this.generation.breath;
    void this.runner.tickInScope(
      PULSE_SCOPE,
      (elapsedSeconds) => {
        if (breath === this.generation.breath) this.setAmountScale("breath", 1 + (pulseScale - 1) * 0.5 * (1 - Math.cos((2 * Math.PI * elapsedSeconds) / pulseSeconds)));
      },
      () => breath !== this.generation.breath || this.amount.destroyed,
    );
  }

  /** The bump as the count lands; the breathing stops and settles to rest. */
  public async playAmountSettleBump(): Promise<void> {
    const { settleSeconds, settleScale } = this.tuning;
    this.generation.breath += 1;
    this.setAmountScale("breath", 1);
    const bump = ++this.generation.bump;
    await this.runner.tweenProgress(
      settleSeconds,
      (k) => {
        if (bump === this.generation.bump) this.setAmountScale("bump", 1 + (settleScale - 1) * Math.sin(Math.PI * k));
      },
      undefined,
      { easing: "linear" },
    );
  }

  /** One of the number's scale factors; the number shows their product. */
  private setAmountScale(channel: keyof BigWinView["amountScale"], value: number): void {
    this.amountScale[channel] = value;
    if (this.amount.destroyed) return;
    const { pop, breath, bump } = this.amountScale;
    this.amount.scale.set(pop * breath * bump, pop * breath * bump);
  }

  public async fadeInContinueText(): Promise<void> {
    this.chrome.stopFade(this.chrome.continueTextRoot);
    this.chrome.continueTextRoot.alpha = 0;
    await this.chrome.fade(this.chrome.continueTextRoot, 1, this.tuning.continueFadeInSeconds);
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
